import { isValidPhoneNumber } from "libphonenumber-js";
/**
 * Flattens a nested error object into a flat structure using dot notation.
 * Useful for accessing all properties of nested errors in form handling (e.g., React Hook Form).
 *
 * @param {Object} errors - The nested error object to be flattened.
 * @param {string} [prefix=''] - The prefix for each nested key.
 * @returns {Object} A flattened error object with dot notation keys.
 */
export const flattenErrors = (errors, prefix = "") => {
  let result = {};

  for (let key in errors) {
    if (errors.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;

      // Check if the current value is an object (error object)
      if (typeof errors[key] === "object" && errors[key] !== null && !errors[key].message) {
        // Recursively flatten if it's an object, but not the actual error object (has message, type, etc.)
        Object.assign(result, flattenErrors(errors[key], newKey));
      } else {
        // Add the entire error object, not just the message
        result[newKey] = errors[key];
      }
    }
  }

  return result;
};

export const phoneValidation = (country = "IN") => ({
  validate: (value) => !value || isValidPhoneNumber(value, country) || "Invalid phone number",
});

const REQUEST_PARTS = ["body", "query", "path"];

/**
 * The form field name inside a FastAPI `loc`, or a dotted field string.
 *
 * Only the LEADING segment is dropped, only when it names a request part, and
 * only when something is left after it. Two cases make all three conditions
 * necessary, and both involve a field literally called `body`:
 *
 * - `["body", "body"]` is FastAPI saying the `body` field of the request body.
 *   Filtering every occurrence reduces it to nothing, so the one field in this
 *   app most likely to fail validation is the one whose error loses its name.
 * - `"body"` on its own is this app's handler naming that field directly. There
 *   is no prefix to drop, and dropping it leaves nothing.
 *
 * @param {string[]|string} location
 * @returns {string} the field name, or "" when the location names no field
 */
export const fieldNameFromLocation = (location) => {
  const parts = (Array.isArray(location) ? location : String(location ?? "").split(".")).map(String).filter(Boolean);
  const rest = REQUEST_PARTS.includes(parts[0]) && parts.length > 1 ? parts.slice(1) : parts;
  return rest[rest.length - 1] ?? "";
};

const humanizeField = (field) =>
  fieldNameFromLocation(field)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || "";

const humanizeValidationText = (message) =>
  String(message || "")
    .replace(/\b(?:body|query|path)\s*->\s*/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Format API error message safely
 *
 * @param {Object} error - API error object
 * @param {string} defaultErrorMessage - Fallback error message
 * @returns {string} Formatted error message
 */
export const apiErrorMessageFormat = (error, defaultErrorMessage = "Something went wrong") => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return humanizeValidationText(data);

  const apiMessage = data?.message ?? data?.error;
  if (typeof apiMessage === "string" && apiMessage.trim()) return humanizeValidationText(apiMessage);

  const validationErrors = Array.isArray(data?.errors) ? data.errors : Array.isArray(data?.detail) ? data.detail : [];
  const firstValidationError = validationErrors.find((item) => typeof (item?.message || item?.msg) === "string" && (item.message || item.msg).trim());

  if (firstValidationError) {
    const field = firstValidationError.field || (Array.isArray(firstValidationError.loc) ? firstValidationError.loc.join(".") : "");
    const fieldLabel = humanizeField(field);
    const message = humanizeValidationText(firstValidationError.message || firstValidationError.msg);
    return fieldLabel ? `${fieldLabel}: ${message}` : message;
  }

  if (typeof data?.detail === "string" && data.detail.trim()) return humanizeValidationText(data.detail);

  return defaultErrorMessage;
};

/**
 * The field-level errors in an API failure, as `{fieldName: message}`.
 *
 * FastAPI answers a validation failure with `detail: [{loc: ["body", "body"],
 * msg, ...}]`, and this app's own handler sometimes sends `errors: [{field,
 * message}]`. Both shapes come back here as one map keyed by the last real
 * segment of the location, with `body` / `query` / `path` dropped, so the key is
 * the name the form registered the field under.
 *
 * `only` narrows it to the fields a given form actually renders. Without that a
 * 422 naming a field the form has no input for would be routed to nothing and
 * disappear, which is worse than a toast.
 *
 * Empty means "nothing here belongs on a field" — a 403, a 500, a domain error
 * with an `error_code`. The caller falls back to `apiErrorMessageFormat` and a
 * toast.
 *
 * @param {Object} error  an axios error
 * @param {string[]} [only]  field names the form has inputs for
 * @returns {Object<string, string>}
 */
export const serverFieldErrors = (error, only = null) => {
  const data = error?.response?.data;
  const raw = Array.isArray(data?.errors) ? data.errors : Array.isArray(data?.detail) ? data.detail : [];
  const allowed = only ? new Set(only) : null;

  return raw.reduce((found, item) => {
    const field = fieldNameFromLocation(item?.field ?? item?.loc ?? []);
    const message = humanizeValidationText(item?.message ?? item?.msg);
    if (!field || !message) return found;
    if (allowed && !allowed.has(field)) return found;

    // The first message for a field wins; a field with two failures shows the
    // first, the same as the client-side resolver does.
    if (!(field in found)) found[field] = message;
    return found;
  }, {});
};

/**
 * Put an API failure's field errors onto the form, and say whether it found any.
 *
 * `false` means the caller still owes the user a sentence — show the toast.
 * `true` means the message is on the field, and a toast would repeat it.
 *
 * @param {Object} error  an axios error
 * @param {Function} setError  react-hook-form's `setError`
 * @param {string[]} [only]  field names the form has inputs for
 * @returns {boolean}
 */
export const applyServerFieldErrors = (error, setError, only = null) => {
  const found = serverFieldErrors(error, only);
  const fields = Object.keys(found);
  if (!fields.length) return false;

  fields.forEach((field, index) => {
    // The first bad field takes focus.
    setError(field, { type: "server", message: found[field] }, index === 0 ? { shouldFocus: true } : undefined);
  });
  return true;
};
